import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Building2, Factory, Warehouse, Truck, Zap, FileCheck, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const IndustrialEPC = () => {
  const industrialTypes = [
    { icon: Warehouse, title: 'Warehouses', desc: 'Distribution centres, storage facilities and logistics hubs' },
    { icon: Factory, title: 'Factories', desc: 'Manufacturing plants and production facilities' },
    { icon: Building2, title: 'Workshops', desc: 'Light industrial units and trade workshops' },
    { icon: Truck, title: 'Distribution Centres', desc: 'Logistics operations and fulfilment centres' }
  ];

  const faqItems = [
    {
      question: 'Do industrial buildings require an EPC in England?',
      answer: 'Yes, all industrial buildings in England require an Energy Performance Certificate when being sold, let or constructed. This includes warehouses, factories, workshops and distribution centres. The requirement applies to buildings over 50m² with a roof and walls that use energy to condition the indoor environment.'
    },
    {
      question: 'When is a warehouse EPC legally required?',
      answer: 'A warehouse EPC is legally required when selling or letting the property, when construction is completed on a new warehouse, when making material alterations that affect energy performance, and for MEES compliance checks on rental properties. Landlords must ensure rental warehouses achieve at least an E rating.'
    },
    {
      question: 'How long does an industrial EPC assessment take?',
      answer: 'The site survey for a typical industrial building takes 1-3 hours depending on size and complexity. Small workshops may take under an hour, while large distribution centres with multiple heating zones may require half a day. The certificate is typically delivered within 5-7 working days of the site visit.'
    },
    {
      question: 'How is an industrial EPC rating calculated?',
      answer: 'Industrial EPCs are calculated using SBEM (Simplified Building Energy Model) methodology. The assessment considers building fabric (walls, roof, floor, doors), heating systems (gas heaters, radiant panels, warm air), lighting (high-bay, fluorescent, LED), ventilation, and any renewable energy systems. The rating reflects the building\'s inherent energy efficiency, not operational usage.'
    },
    {
      question: 'What is SBEM modelling for industrial buildings?',
      answer: 'SBEM (Simplified Building Energy Model) is the government-approved software used to calculate energy performance of non-domestic buildings including industrial premises. It models the building\'s construction, heating, lighting and ventilation systems to produce a standardised energy rating from A (most efficient) to G (least efficient).'
    },
    {
      question: 'What EPC rating do industrial buildings typically achieve?',
      answer: 'Industrial buildings commonly achieve ratings between D and F due to characteristics like minimal insulation, large roller shutter doors, high ceilings and industrial heating systems. Older steel-frame warehouses often rate E or F, while modern insulated units with LED lighting may achieve C or D ratings.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Industrial EPC Assessment",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "telephone": "+44-1392-980077",
      "email": "info@energyperformancedirect.co.uk",
      "areaServed": ["Devon", "Cornwall", "Somerset", "Dorset"]
    },
    "areaServed": {
      "@type": "Place",
      "name": "South West England"
    },
    "description": "Professional Energy Performance Certificates for industrial buildings including warehouses, factories, workshops and distribution centres across Devon, Cornwall, Somerset and Dorset."
  };

  return (
    <>
      <Helmet>
        <title>Industrial EPC Assessments | Warehouse & Factory EPCs | South West UK</title>
        <meta name="description" content="Expert Industrial EPC assessments for warehouses, factories, workshops and distribution centres across Devon, Cornwall, Somerset and Dorset. SBEM certified assessors. Fast turnaround." />
        <meta name="keywords" content="industrial EPC, warehouse EPC, factory EPC, workshop EPC, commercial EPC, SBEM assessment, Devon, Cornwall, Somerset, Dorset, South West" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/industrial-epc" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }]} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm mb-6">
              <Factory className="w-4 h-4" />
              Industrial EPC Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Industrial EPC Assessments
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Warehouses • Factories • Workshops • Distribution Centres
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl mx-auto">
              Professional Energy Performance Certificates for industrial buildings across Devon, Cornwall, Somerset and Dorset. Our accredited assessors specialise in non-domestic EPC surveys for warehouses, factories, workshops and logistics facilities throughout the South West.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">
                  Get Industrial EPC Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 01392 980077
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Energy Performance Certificates for Industrial Buildings</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                Industrial buildings present unique challenges for energy assessment. Unlike standard commercial offices or retail units, warehouses, factories and workshops often feature high ceilings, minimal insulation, large roller shutter doors, industrial heating systems and specialised lighting configurations. These characteristics require assessors with specific expertise in non-domestic energy modelling using SBEM (Simplified Building Energy Model) methodology.
              </p>
              <p>
                Energy Performance Direct provides professional industrial EPC assessments throughout South West England, covering Devon, Cornwall, Somerset and Dorset. Our accredited non-domestic energy assessors understand the complexities of industrial buildings—from cold storage warehouses with refrigeration systems to heated manufacturing facilities with process equipment, from small trade workshops to large distribution centres spanning thousands of square metres.
              </p>
              <p>
                An industrial EPC is legally required when selling, letting or constructing any non-domestic building in England and Wales. For landlords, the Minimum Energy Efficiency Standards (MEES) regulations (Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015) require that all rental properties achieve at least Band E. Properties rated F or G cannot be legally let without a registered exemption. Financial penalties are calculated on rateable value and breach duration (Regulation 39), up to a statutory maximum set out in the Regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industrial Types */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Industrial Buildings We Assess</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {industrialTypes.map((type, index) => (
                <Card key={index} className="bg-white">
                  <CardContent className="p-6 text-center">
                    <type.icon className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{type.title}</h3>
                    <p className="text-gray-600 text-sm">{type.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* When EPC Required */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">When is an Industrial EPC Required?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Legal Requirements</h3>
                <ul className="space-y-3">
                  {[
                    'Selling an industrial property',
                    'Letting or renewing a commercial lease',
                    'Completing construction of a new building',
                    'Making material alterations affecting energy use',
                    'MEES compliance for rental properties'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">MEES Compliance</h3>
                <p className="text-gray-700 mb-4">
                  Since April 2023, all non-domestic rental properties must achieve a minimum EPC rating of E. Industrial landlords must ensure their warehouses, factories and workshops meet this standard or register a valid exemption.
                </p>
                <p className="text-gray-700">
                  Financial penalties are calculated on rateable value and breach duration (Regulation 39 of the Energy Efficiency (Private Rented Property) (England and Wales) Regulations 2015), up to the statutory maximum. Our <Link to="/mees-commercial-buildings" className="text-green-600 hover:underline">MEES compliance guidance</Link> explains the regulations and exemption process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SBEM Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-orange-400" />
              <h2 className="text-3xl font-bold">SBEM Modelling for Industrial Buildings</h2>
            </div>
            <div className="prose prose-lg prose-invert max-w-none">
              <p>
                Industrial EPCs are calculated using SBEM (Simplified Building Energy Model), the government-approved methodology for non-domestic buildings. Our assessors input detailed information about your building's construction, heating systems, lighting and ventilation into specialised software that calculates standardised energy performance.
              </p>
              <p>
                For industrial buildings, the SBEM assessment considers factors including:
              </p>
              <ul>
                <li><strong>Building fabric:</strong> Wall insulation, roof construction, floor thermal performance, glazing ratios and air permeability</li>
                <li><strong>Heating systems:</strong> Gas-fired radiant heaters, warm air systems, destratification fans, unit heaters and heating controls</li>
                <li><strong>Lighting:</strong> High-bay lighting, fluorescent vs LED, lighting controls and daylight factors</li>
                <li><strong>Ventilation:</strong> Natural ventilation, mechanical extraction, HVAC systems for conditioned spaces</li>
                <li><strong>Renewable energy:</strong> Solar PV installations, biomass heating, heat pumps</li>
              </ul>
              <p>
                Learn more about <Link to="/sbem-explained" className="text-orange-400 hover:underline">SBEM calculations</Link> and <Link to="/how-commercial-epc-calculated" className="text-orange-400 hover:underline">how commercial EPCs are calculated</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Factors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Factors Affecting Industrial EPC Ratings</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-semibold">Lighting Systems</h3>
                </div>
                <p className="text-gray-700">
                  Industrial lighting significantly impacts EPC ratings. Traditional high-bay sodium or metal halide fixtures consume substantial energy. Upgrading to LED high-bay lighting can improve ratings by 10-20 points while reducing energy costs by 50-70%.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Factory className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-semibold">Heating Efficiency</h3>
                </div>
                <p className="text-gray-700">
                  Industrial heating systems range from gas-fired radiant panels to warm air heaters. Older, inefficient units with poor controls negatively affect ratings. Modern condensing heaters with zoning controls can substantially improve energy performance.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-6 h-6 text-orange-600" />
                  <h3 className="text-xl font-semibold">Building Fabric</h3>
                </div>
                <p className="text-gray-700">
                  Poor insulation, uninsulated roller shutter doors, single-skin cladding and thermal bridging at junctions all reduce EPC ratings. Roof insulation upgrades and door seals offer cost-effective improvements for industrial buildings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Industrial EPC Coverage Across the South West</h2>
            <p className="text-center text-gray-700 mb-8">
              We provide industrial EPC assessments throughout Devon, Cornwall, Somerset and Dorset, covering major industrial areas and business parks.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/industrial-epc-devon" className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-lg mb-2">Devon</h3>
                <p className="text-gray-600 text-sm">Exeter, Plymouth, Barnstaple, Newton Abbot, Tiverton</p>
              </Link>
              <Link to="/industrial-epc-cornwall" className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-lg mb-2">Cornwall</h3>
                <p className="text-gray-600 text-sm">Truro, Newquay, Bodmin, Camborne, Redruth</p>
              </Link>
              <Link to="/industrial-epc-somerset" className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-lg mb-2">Somerset</h3>
                <p className="text-gray-600 text-sm">Taunton, Bridgwater, Yeovil, Frome, Glastonbury</p>
              </Link>
              <Link to="/industrial-epc-dorset" className="block p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-lg mb-2">Dorset</h3>
                <p className="text-gray-600 text-sm">Dorchester, Weymouth, Poole, Bournemouth, Blandford</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Explore Our Industrial EPC Services</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/warehouse-epc" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Warehouse className="w-8 h-8 text-orange-600 mb-3" />
                <h3 className="font-semibold text-lg mb-2">Warehouse EPCs</h3>
                <p className="text-gray-600 text-sm">Distribution centres, storage facilities and logistics hubs</p>
              </Link>
              <Link to="/workshop-epc" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Building2 className="w-8 h-8 text-orange-600 mb-3" />
                <h3 className="font-semibold text-lg mb-2">Workshop EPCs</h3>
                <p className="text-gray-600 text-sm">Light industrial units, trade workshops and service centres</p>
              </Link>
              <Link to="/factory-epc" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Factory className="w-8 h-8 text-orange-600 mb-3" />
                <h3 className="font-semibold text-lg mb-2">Factory EPCs</h3>
                <p className="text-gray-600 text-sm">Manufacturing plants and production facilities</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Industrial EPC Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Get Your Industrial EPC Quote</h2>
            <p className="text-xl text-green-100 mb-8">
              Professional assessments for warehouses, factories, workshops and distribution centres across Devon, Cornwall, Somerset and Dorset.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
                <Link to="/booking?service=commercial_warehouse">
                  Request Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077">
                  <Phone className="mr-2 w-5 h-5" />
                  01392 980077
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial EPC Case Studies</h2>
            <Link to="/case-study-exeter-office-workshop" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200" data-testid="case-study-link">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg mb-1">Office and Workshop Near Exeter</h3>
                  <p className="text-gray-600 text-sm">793.5 sq m mixed-use building assessed with full Energy Advice Report. Staged improvement pathway from G to A rating, including oil boiler replacement and LED upgrade recommendations.</p>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 flex-shrink-0" />
              </div>
            </Link>
            <Link to="/case-study-exeter-workshop-refurbishment" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200 mt-4" data-testid="case-study-link-2">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg mb-1">Twin Workshop Units Refurbishment Near Exeter</h3>
                  <p className="text-gray-600 text-sm">185.7 sq m adjacent industrial units. Pre-refurbishment SBEM modelling achieving Band C with pathway to B41 through targeted ceiling insulation and weather compensation controls.</p>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 flex-shrink-0" />
              </div>
            </Link>
            <Link to="/case-study-exeter-office-g-to-a" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200 mt-4" data-testid="case-study-link-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg mb-1">Period Office Building: G189 to A25 in Exeter</h3>
                  <p className="text-gray-600 text-sm">364.9 sq m period office building with five-option improvement roadmap. Complete transformation from worst to best EPC band with financial payback analysis and MEES compliance assessment.</p>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 flex-shrink-0" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Learn More About Commercial EPCs</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/services/commercial_warehouse" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> Commercial EPC Services
              </Link>
              <Link to="/sbem-explained" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> SBEM Explained
              </Link>
              <Link to="/mees-commercial-buildings" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> MEES Regulations Guide
              </Link>
              <Link to="/industrial-epc-requirements" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> Industrial EPC Requirements
              </Link>
              <Link to="/how-commercial-epc-calculated" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> How Commercial EPCs are Calculated
              </Link>
              <Link to="/improving-commercial-epc-rating" className="flex items-center gap-2 text-green-600 hover:underline">
                <ArrowRight className="w-4 h-4" /> Improving Your EPC Rating
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustrialEPC;
